import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { listenInOwnerDocument } from '../shadowDom';
import { PALETTE } from '../designPalette';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
export const COMMENT_EMOJIS = [
    ['😀', 'Grinning face'], ['😊', 'Smiling face'], ['😂', 'Laughing'], ['😍', 'Heart eyes'],
    ['🤔', 'Thinking'], ['😅', 'Relieved smile'], ['👍', 'Thumbs up'], ['👎', 'Thumbs down'],
    ['👏', 'Clapping'], ['🙌', 'Celebration'], ['🙏', 'Thank you'], ['👀', 'Eyes'],
    ['❤️', 'Heart'], ['🎉', 'Party'], ['✨', 'Sparkles'], ['🚀', 'Rocket'],
    ['✅', 'Check mark'], ['❌', 'Cross mark'], ['⚠️', 'Warning'], ['💡', 'Idea'],
];
export function CommentEmojiPicker({ textareaRef, onChange, disabled = false, buttonClassName }) {
    const [open, setOpen] = useState(false);
    const root = useRef(null);
    const trigger = useRef(null);
    const menuRef = useRef(null);
    const selection = useRef({ start: 0, end: 0 });
    useEffect(() => {
        const node = root.current;
        const dismiss = () => { setOpen(false); trigger.current?.focus(); };
        node?.addEventListener('oq:emoji-dismiss', dismiss);
        return () => node?.removeEventListener('oq:emoji-dismiss', dismiss);
    }, []);
    useEffect(() => {
        if (!open || disabled)
            return;
        const menu = menuRef.current;
        if (!(root.current?.getRootNode() instanceof ShadowRoot) && menu.showPopover) {
            menu.setAttribute('popover', 'manual');
            Object.assign(menu.style, { inset: 'auto', margin: '0' });
            menu.showPopover();
        }
        const anchor = trigger.current.getBoundingClientRect();
        const size = menu.getBoundingClientRect();
        menu.style.left = `${Math.max(8, Math.min(anchor.left, window.innerWidth - size.width - 8))}px`;
        menu.style.top = `${Math.max(8, anchor.bottom + size.height + 4 > window.innerHeight ? anchor.top - size.height - 4 : anchor.bottom + 4)}px`;
        menu.querySelector('[data-emoji]')?.focus();
        const dismiss = () => { setOpen(false); trigger.current?.focus(); };
        menu.addEventListener('oq:emoji-dismiss', dismiss);
        const outside = (event) => {
            if (!event.composedPath().includes(root.current) && !event.composedPath().includes(menu))
                setOpen(false);
        };
        const stopOutside = listenInOwnerDocument(menu, 'pointerdown', outside);
        return () => { stopOutside(); menu.removeEventListener('oq:emoji-dismiss', dismiss); };
    }, [open, disabled]);
    // Keep extension menus in their shadow root so capture's visibility toggle
    // covers the chooser too (utils/hideOverlayDuringCapture.ts:51).
    // The extension host disables pointer events (overlay-initializer.tsx:28),
    // so the portalled menu explicitly restores them below.
    // mouseInteractionHelpers.ts:12 and useHoverPreview.ts:137 use data-popover
    // to keep portalled UI gestures from creating a capture on the page below.
    const surfaceRoot = root.current?.getRootNode();
    const renderMenu = (menu) => surfaceRoot instanceof ShadowRoot ? createPortal(menu, surfaceRoot) : menu;
    return _jsxs("div", { ref: root, "data-comment-emoji-open": open ? 'true' : undefined, style: { position: 'relative', display: 'inline-block' }, onBlur: event => { if (!event.currentTarget.contains(event.relatedTarget) && !menuRef.current?.contains(event.relatedTarget))
            setOpen(false); }, onKeyDown: event => {
            if (!open)
                return;
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
                trigger.current?.focus();
            }
            const buttons = Array.from(menuRef.current.querySelectorAll('[data-emoji]'));
            const index = buttons.indexOf(event.target);
            if (index < 0)
                return;
            const offset = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 5, ArrowUp: -5 }[event.key];
            if (offset !== undefined) {
                event.preventDefault();
                event.stopPropagation();
                buttons[(index + offset + buttons.length) % buttons.length].focus();
            }
        }, children: [_jsx("button", { ref: trigger, type: "button", className: buttonClassName, "aria-label": "Add emoji", title: "Add emoji", "aria-expanded": open, "aria-haspopup": "dialog", disabled: disabled, style: buttonClassName ? undefined : { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, minHeight: 32, boxSizing: 'border-box', padding: 0, border: "1px solid " + PALETTE.neutral[200], borderRadius: 6, background: PALETTE.surface.card, color: PALETTE.neutral[600], cursor: disabled ? 'default' : 'pointer' }, onClick: () => {
                    const textarea = textareaRef.current;
                    if (textarea)
                        selection.current = { start: textarea.selectionStart, end: textarea.selectionEnd };
                    setOpen(!open);
                }, children: _jsxs("svg", { "aria-hidden": "true", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "9" }), _jsx("path", { d: "M8 14s1.5 3 4 3 4-3 4-3" }), _jsx("path", { d: "M8 9h.01M16 9h.01", strokeWidth: "3" })] }) }), open && !disabled && renderMenu(_jsx("div", { ref: menuRef, "data-popover": surfaceRoot instanceof ShadowRoot ? '' : undefined, "data-comment-emoji-open": "true", role: "dialog", "aria-label": "Choose an emoji", style: { position: 'fixed', top: 0, left: 0, zIndex: 2147483647, pointerEvents: 'auto', display: 'grid', gridTemplateColumns: 'repeat(5, 32px)', gap: 4, padding: 8, background: PALETTE.surface.card, border: "1px solid " + PALETTE.neutral[200], borderRadius: 8, boxShadow: '0 4px 12px #0002' }, children: COMMENT_EMOJIS.map(([emoji, label]) => _jsx("button", { type: "button", "data-emoji": true, "aria-label": label, style: { width: 32, height: 32, border: 0, borderRadius: 4, background: 'transparent', fontSize: 20, cursor: 'pointer' }, onClick: () => {
                        const textarea = textareaRef.current;
                        if (!textarea)
                            return;
                        const { start, end } = selection.current;
                        const next = textarea.value.slice(0, start) + emoji + textarea.value.slice(end);
                        if (textarea.maxLength >= 0 && next.length > textarea.maxLength)
                            return;
                        onChange(next);
                        setOpen(false);
                        requestAnimationFrame(() => { textarea.focus(); textarea.setSelectionRange(start + emoji.length, start + emoji.length); });
                    }, children: emoji }, label)) }))] });
}
//# sourceMappingURL=CommentEmojiPicker.js.map